import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  Palette,
  Monitor,
  Megaphone,
  BookOpen,
  PenTool,
  Video,
  Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PremiumServiceCard } from '@/components/PremiumServiceCard';
import ProceduralGroundBackground from '@/components/ui/procedural-ground-background';
import { ClientsSection } from '@/components/ClientsSection';
import { useServices } from '@/hooks/use-services';
import { motion } from 'framer-motion';

export default function Servicos() {
  const { data: services, isLoading, error } = useServices();

  const getServiceIcon = (name: string) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('branding') || lowerName.includes('identidade')) return Palette;
    if (lowerName.includes('ui/ux') || lowerName.includes('interface') || lowerName.includes('web')) return Monitor;
    if (lowerName.includes('direção') || lowerName.includes('campanha') || lowerName.includes('marketing')) return Megaphone;
    if (lowerName.includes('editorial') || lowerName.includes('diagramação')) return BookOpen;
    if (lowerName.includes('ilustração') || lowerName.includes('arte')) return PenTool;
    if (lowerName.includes('motion') || lowerName.includes('vídeo') || lowerName.includes('video')) return Video;
    return Sparkles;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 to-slate-900">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full mx-auto mb-4"
          />
          <p className="text-lg text-gray-400">Carregando serviços...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 to-slate-900">
        <div className="text-center max-w-md px-4">
          <p className="text-lg text-gray-400 mb-6">
            Erro ao carregar serviços. Tente novamente mais tarde.
          </p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-foreground overflow-x-hidden relative">
      <ProceduralGroundBackground />

      {/* Header */}
      <section className="pt-24 pb-12 relative overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] opacity-20 pointer-events-none">
          <div className="w-full h-full bg-blue-500/20 blur-[100px] rounded-full" />
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white tracking-tight">
            Nossos <span className="gradient-text-primary">Serviços</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto font-light">
            Oferecemos soluções completas de design para transformar sua marca e conectar com seu público.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-10 pb-32">
        <div className="container mx-auto px-4">

          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Como posso ajudar seu projeto</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Serviços especializados em design visual e estratégia criativa para marcas que querem impactar
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {services?.map((service, index) => (
              <PremiumServiceCard
                key={service.id}
                index={index}
                title={service.name}
                description={service.summary}
                price={service.startingPrice || 'Sob consulta'}
                icon={getServiceIcon(service.name)}
                deliverables={service.deliverables}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <ClientsSection />

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-8 text-white">
            Pronto para Transformar Sua Marca?
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">
            Vamos conversar sobre seu projeto e descobrir como podemos elevar sua presença no mercado.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button size="lg" className="liquid-glass-btn-solid rounded-full px-8 h-12 text-lg" asChild>
              <Link to="/contato">
                Solicitar Orçamento
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" className="liquid-glass-outline rounded-full px-8 h-12 text-lg" asChild>
              <Link to="/projetos">Ver Portfólio</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}